/**
 * Grille tarifaire des **mises à disposition** avec chauffeur — tableau
 * sémantique, crawlable, lisible par Google et les moteurs de réponse.
 *
 * Source unique : `VEHICLES[].disposalPackages` (app/config/infos.ts), le
 * MÊME registre que celui consommé par le moteur d'estimation de la
 * réservation (`lib/pricing/estimateReservationPrice.ts`). Aucun montant en
 * dur ici : la grille publiée ne peut donc pas diverger du prix réellement
 * calculé au devis — c'est tout l'intérêt, un tarif affiché qui ne correspond
 * pas au devis coûte plus cher qu'un tarif absent.
 *
 * Le minimum de 3 h et le mécanisme d'heure supplémentaire reproduisent la
 * règle du moteur (`Math.max(3, duration)` puis `extraHours * extraHour`).
 *
 * Composant serveur. i18n FR/EN/AR, classes logiques (RTL-safe).
 */
import { VEHICLES, type Vehicle } from "@/config/infos";
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { formatEuro, formatEuroPerKm } from "./formatPrice";

type Dict = {
  caption: string;
  vehicle: string;
  passengers: string;
  hour: (n: number) => string;
  extraHour: string;
  perKm: string;
  note: string;
};

const UI: I18n<Dict> = {
  fr: {
    caption:
      "Tarifs des mises à disposition avec chauffeur, par classe de véhicule et par durée",
    vehicle: "Véhicule",
    passengers: "Pass.",
    hour: (n) => `${n} h`,
    extraHour: "Heure suppl.",
    perKm: "Au-delà",
    note: "Durée minimum de 3 h. Au-delà du forfait retenu, chaque heure entamée est facturée au tarif « heure supplémentaire » de la classe. La colonne « Au-delà » indique le tarif kilométrique appliqué aux trajets sans forfait fixe.",
  },
  en: {
    caption: "Hourly chauffeur hire rates, by vehicle class and duration",
    vehicle: "Vehicle",
    passengers: "Pax",
    hour: (n) => `${n} h`,
    extraHour: "Extra hour",
    perKm: "Beyond",
    note: "Three-hour minimum. Past the selected package, every additional hour is billed at the class's extra-hour rate. The “Beyond” column shows the per-kilometre rate applied to journeys with no fixed transfer price.",
  },
  ar: {
    caption: "أسعار وضع سيارة مع سائق تحت التصرّف، حسب فئة السيارة والمدة",
    vehicle: "السيارة",
    passengers: "ركّاب",
    hour: (n) => `${n} ساعات`,
    extraHour: "ساعة إضافية",
    perKm: "لكل كم",
    note: "الحد الأدنى ثلاث ساعات. بعد انتهاء الباقة المختارة، تُحتسب كل ساعة إضافية بسعر الساعة الإضافية الخاص بالفئة. عمود «لكل كم» يوضّح السعر المطبَّق على الرحلات التي لا يشملها سعر ثابت.",
  },
};

/**
 * Ordre d'affichage : de la classe la plus accessible à la plus haute.
 *
 * `CLASS_SECURITY` (véhicule blindé) est volontairement ABSENT. Les tableaux
 * de prix publics existants du silo transferts s'arrêtent déjà à la Range
 * (`VEHICLE_DISPLAY_ORDER` dans data/transfers) : publier une grille pour une
 * prestation de sécurité est un choix commercial, pas une évidence technique.
 * Elle reste sélectionnable au devis. Ajouter `"CLASS_SECURITY"` ici suffit à
 * la publier.
 *
 * Le Sprinter VIP, lui, est inclus : 16 places, il répond à une demande
 * réellement mesurée en recherche (« location minibus cannes », « location
 * voiture 6 places »…).
 */
const DISPLAY_ORDER: Vehicle["id"][] = [
  "CLASS_E",
  "CLASS_V",
  "CLASS_S",
  "CLASS_R",
  "CLASS_SPRINTER",
];

/** Durées de forfait publiées — dérivées des données, jamais écrites en dur. */
const durations = (): number[] => {
  const set = new Set<number>();
  for (const vehicle of Object.values(VEHICLES)) {
    for (const pkg of vehicle.disposalPackages) set.add(pkg.hours);
  }
  return [...set].sort((a, b) => a - b);
};

/** Plage des heures supplémentaires d'une classe (le tarif baisse sur 12/14 h). */
const extraHourRange = (vehicle: Vehicle): string => {
  const rates = vehicle.disposalPackages.map((p) => p.extraHour);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  return min === max
    ? formatEuro(min)
    : `${formatEuro(min)} – ${formatEuro(max)}`;
};

export function DisposalPricingTable({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const t = tr(UI, locale);
  const hours = durations();

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
        <table className="w-full min-w-[46rem] border-collapse text-start text-sm">
          <caption className="sr-only">{t.caption}</caption>
          <thead>
            <tr className="border-b border-black/10 bg-black/[0.03] text-xs uppercase tracking-[0.12em] text-black/60">
              <th scope="col" className="px-4 py-3 text-start font-semibold">
                {t.vehicle}
              </th>
              <th scope="col" className="px-3 py-3 text-end font-semibold">
                {t.passengers}
              </th>
              {hours.map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="px-3 py-3 text-end font-semibold"
                >
                  {t.hour(h)}
                </th>
              ))}
              <th scope="col" className="px-3 py-3 text-end font-semibold">
                {t.extraHour}
              </th>
              <th scope="col" className="px-4 py-3 text-end font-semibold">
                {t.perKm}
              </th>
            </tr>
          </thead>
          <tbody>
            {DISPLAY_ORDER.map((id) => {
              const vehicle = VEHICLES[id];
              return (
                <tr key={id} className="border-b border-black/5 last:border-0">
                  <th
                    scope="row"
                    className="whitespace-nowrap px-4 py-3 text-start font-medium text-black/90"
                  >
                    {vehicle.name}
                  </th>
                  <td className="px-3 py-3 text-end text-black/70">
                    {vehicle.maxPassengers}
                  </td>
                  {hours.map((h) => {
                    const pkg = vehicle.disposalPackages.find(
                      (p) => p.hours === h,
                    );
                    return (
                      <td
                        key={h}
                        className="whitespace-nowrap px-3 py-3 text-end font-semibold text-black"
                      >
                        {pkg ? formatEuro(pkg.price) : "—"}
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap px-3 py-3 text-end text-black/70">
                    {extraHourRange(vehicle)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-end text-black/70">
                    {formatEuroPerKm(vehicle.pricePerKm)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-black/65">{t.note}</p>
    </div>
  );
}

export default DisposalPricingTable;
